// client/src/components/pages/LexiStar/StatusBoard/atoms.tsx (converted from .jsx, ST-00204)
//
// Small presentational pieces shared by the LexiStar control-tower pages
// (status board, approvals, trip detail, plan & dispatch): status and stage
// pills, provenance marker, burn index, over-consumption tag, run chip, data
// age, severity chip, banners and page states. Styling follows the M1 LexiStar
// Assets page (slate chrome, primary accents, rounded-xl cards).
//
// ST-00204: DateTimeField (planned start, override "until", corridor expiry, split point, "who
// was driving at") now renders through the shared UnifiedDatePicker — a field opening the date +
// time popup on a desktop (ST-00294), a thumb-sized bottom sheet on a phone.
//
// ST-00206: AckRemindersTag — "reminded ×N" next to a DISPATCHED trip's stage (the status
// board's table and cards): the driver has not accepted and LexiStar has reminded them.
//
// ST-00205: AwaitingReportTag — "waiting for first report" next to a DISPATCHED trip whose
// truck has not reported a position since the dispatch: the driver already has the message;
// tracking and the stages start when the tracker next reports.
//
// ST-00207 (phone pass): "How was this calculated?" is a 44 px target on a phone or a touch
// screen (.touch-target, no change with a mouse); ReasonCodeSelect takes a className and
// isSearchable for the phone layouts; RunChip takes a className; ConnectionIndicator has a
// compact form for the phone headers.
//
// ST-00253 / ST-00244: StatusPill takes its colour from the shared asset status scheme
// (map/assetStatus) — the row's control room key when the caller has the row (statusKey), so the
// pill matches the truck's colour on the control room, Live View and Replay.

import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import UnifiedDatePicker from '../../../common/UnifiedDatePicker';
import type { TimePreset } from '../../../common/pickerLogic';
import ThemedSelectJs from '../../../common/ThemedSelect';
import PageHeaderJs from '../../../common/PageHeader';
// Types only: the facade module itself pulls in the map, which these atoms must not load.
import type { PageHeaderProps, ThemedSelectProps } from '../../../../types/commonComponents';
import { listReasonCodes, pickList } from '../../../../services/lexistarStatusBoard';
import {
  TONES as TONES_JS,
  OFFLINE_GLYPH,
  OVERLAY_META as OVERLAY_META_JS,
  STAGE_LABELS as STAGE_LABELS_JS,
  combinedStatusLabel,
  isOffline,
  stagePill,
  severityMeta,
} from './statusLanguage';
import {
  ASSET_STATUS,
  ASSET_STATUS_CLASSES,
  statusKeyOfStatus,
  type AssetStatusKey,
} from '../../../map/assetStatus';
import {
  PROVENANCE_META as PROVENANCE_META_JS,
  normaliseProvenance,
  burnIndexBand,
  BURN_BAND_META as BURN_BAND_META_JS,
  overConsumptionTag,
} from './fuelLogic';
import { dataAge } from './format';
import DisplayMarks from '../../../map/DisplayMarks';
import { DEFAULT_RULE_IDS, type AssetDisplay } from '../../../map/displayRules';

// ── The JS vocabularies these atoms read (statusLanguage.js, fuelLogic.js) ──

interface ToneMeta {
  name: string;
  hex: string;
  pill: string;
  ring: string;
  glyph?: string;
  Icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
}
const TONES = TONES_JS as unknown as Record<string, ToneMeta>;
const OVERLAY_META = OVERLAY_META_JS as unknown as Record<
  string,
  { label: string; glyph: string; className: string }
>;
const STAGE_LABELS = STAGE_LABELS_JS as Record<string, string>;
const PROVENANCE_META = PROVENANCE_META_JS as Record<string, { glyph: string; label: string; help: string }>;
const BURN_BAND_META = BURN_BAND_META_JS as Record<string, { label: string; className: string; arrow: string }>;

/** A truck's two-axis status as the status board / trip detail receive it. */
export interface TwoAxisStatus {
  context?: string | null;
  motion?: string | null;
  overlays?: string[] | null;
  override?: { reason?: string | null; mode?: string | null } | null;
}

export const inputClasses =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

export const cardClasses = 'bg-surface rounded-xl border border-slate-200 shadow-sm';

// ── Status ──────────────────────────────────────────────────────────────────

/** The glyph of a status key (maintenance / breakdown: their icons). */
const Glyph = ({ statusKey }: { statusKey: AssetStatusKey }) => {
  const Icon =
    statusKey === 'maintenance' || statusKey === 'planned_maintenance'
      ? TONES.maintenance?.Icon
      : statusKey === 'breakdown'
        ? TONES.breakdown?.Icon
        : undefined;
  if (Icon) return <Icon size={10} aria-hidden="true" />;
  return (
    <span aria-hidden="true" className="leading-none">
      {ASSET_STATUS[statusKey].glyph}
    </span>
  );
};

/** The default rules' marks the board shows its own way (ack reminders column; the offline tag). */
const PILL_BUILT_IN_MARKS = ['driver-reminded', 'no-recent-position'];

/**
 * Two-axis status pill: colour + glyph + "Context · motion"; hollow when offline. `statusKey`
 * is the truck's key from its whole row (controlRoom statusKeyOfRow); without it the status
 * alone decides (statusKeyOfStatus). ST-00276: with the row's `display`, a colour rule of the
 * organisation's own adds its words ("Low fuel") and its marks follow (map/DisplayMarks).
 */
export function StatusPill({
  status,
  statusKey,
  display = null,
}: {
  status: TwoAxisStatus | null | undefined;
  statusKey?: AssetStatusKey;
  display?: AssetDisplay | null;
}) {
  const key: AssetStatusKey = statusKey ?? statusKeyOfStatus(status);
  const offline = isOffline(status) || key === 'offline';
  const label = combinedStatusLabel(status);
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${ASSET_STATUS_CLASSES[offline ? 'offline' : key].pill}`}
        title={offline ? `${ASSET_STATUS[key].label} (offline)` : ASSET_STATUS[key].label}
        data-tone={key}
      >
        {offline ? (
          <span aria-hidden="true" className="leading-none">
            {OFFLINE_GLYPH}
          </span>
        ) : (
          <Glyph statusKey={key} />
        )}
        {label}
      </span>
      {offline && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">
          Offline
        </span>
      )}
      {(status?.overlays || [])
        .filter((o) => OVERLAY_META[o])
        .map((o) => (
          <span
            key={o}
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${OVERLAY_META[o]!.className}`}
          >
            <span aria-hidden="true">{OVERLAY_META[o]!.glyph}</span>
            {OVERLAY_META[o]!.label}
          </span>
        ))}
      {status?.override && (
        <span
          className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700"
          title={`Manual override: ${status.override.reason || ''}`}
        >
          Override
        </span>
      )}
      {display && !DEFAULT_RULE_IDS.has(display.base.rule_id) && (
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${ASSET_STATUS_CLASSES[key].pill}`}
          data-display-rule={display.base.rule_id}
        >
          {display.base.label}
        </span>
      )}
      <DisplayMarks overlays={display?.overlays} skip={PILL_BUILT_IN_MARKS} />
    </span>
  );
}

export function StagePill({ stage }: { stage?: string | null }) {
  if (!stage) return <span className="text-xs text-slate-400 italic">—</span>;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${stagePill(stage)}`}
    >
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}

/** How many driver acknowledgement reminders a trip shows: its ack_reminders while DISPATCHED, else 0. */
export function ackReminderCount(
  trip: { stage?: string | null; ack_reminders?: number | null } | null | undefined
): number {
  if (!trip || trip.stage !== 'DISPATCHED') return 0;
  const n = Number(trip.ack_reminders);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * "reminded ×N" (ST-00206): the driver has not accepted the DISPATCHED trip and LexiStar has
 * reminded them N times (1 = the reminder, 2 = also the last one when the alert escalated).
 * Nothing when there is no reminder.
 */
export function AckRemindersTag({
  trip,
}: {
  trip: { stage?: string | null; ack_reminders?: number | null } | null | undefined;
}) {
  const n = ackReminderCount(trip);
  if (!n) return null;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200"
      title={
        n >= 2
          ? 'Not accepted yet: the driver was reminded, then got the last reminder when the alert escalated'
          : 'Not accepted yet: the driver was reminded to accept the trip'
      }
      aria-label={`Driver reminded ${n} ${n === 1 ? 'time' : 'times'} to accept`}
      data-reminders={n}
    >
      reminded ×{n}
    </span>
  );
}

/** ST-00205: is the board's trip DISPATCHED and still waiting for its truck's first report? */
export function isAwaitingFirstReport(
  trip: { stage?: string | null; awaiting_first_report?: boolean | null } | null | undefined
): boolean {
  return !!trip && trip.stage === 'DISPATCHED' && trip.awaiting_first_report === true;
}

/**
 * "waiting for first report" (ST-00205): the trip was dispatched and the driver messaged, but
 * the truck's tracker has not reported since — the engine applies the dispatch, and the stages
 * start, at its next position. Nothing otherwise.
 */
export function AwaitingReportTag({
  trip,
}: {
  trip: { stage?: string | null; awaiting_first_report?: boolean | null } | null | undefined;
}) {
  if (!isAwaitingFirstReport(trip)) return null;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200"
      title="Dispatched and the driver has the message, but the truck has not reported a position since: tracking and the trip's stages start when its tracker next reports"
      aria-label="Waiting for the truck's first report since dispatch"
      data-awaiting-report="true"
    >
      waiting for first report
    </span>
  );
}

// ── Fuel & distance ─────────────────────────────────────────────────────────

/** ● Measured ◐ Derived ○ Estimated, with the source in the tooltip. */
export function ProvenanceMarker({
  provenance,
  className = '',
}: {
  provenance?: string | null;
  className?: string;
}) {
  const p: string | null = normaliseProvenance(provenance);
  if (!p) return null;
  const meta = PROVENANCE_META[p];
  if (!meta) return null;
  return (
    <span
      className={`text-slate-500 text-[11px] leading-none cursor-help ${className}`}
      title={meta.help}
      aria-label={meta.label}
      role="img"
    >
      {meta.glyph}
    </span>
  );
}

export function BurnIndex({
  value,
  pctDistance,
}: {
  value: number | string | null | undefined;
  pctDistance?: number | string | null;
}) {
  // fuelLogic's JSDoc says number | null; it reads anything Number() can (and null / NaN as none).
  const band: string = burnIndexBand(value as number | null, (pctDistance ?? null) as number | null);
  const meta = BURN_BAND_META[band]!;
  if (band === 'suppressed') {
    return (
      <span className={meta.className} title={meta.label} data-band={band}>
        —
      </span>
    );
  }
  return (
    <span
      className={`tabular-nums whitespace-nowrap ${meta.className}`}
      title={`Burn index ${Number(value).toFixed(2)} — ${meta.label}`}
      data-band={band}
    >
      {Number(value).toFixed(2)}
      {meta.arrow && <span className="ml-0.5">{meta.arrow}</span>}
    </span>
  );
}

interface OverConsumptionTagView {
  text: string;
  className: string;
  title: string;
  severity: string;
  certainty: string;
}

/** The fuel block of a status-board row / trip (fuelLogic.overConsumptionTag reads it). */
export type FuelBlock = Record<string, unknown>;

export function OverConsumptionTag({ fuel }: { fuel: FuelBlock | null | undefined }) {
  const tag = overConsumptionTag(fuel ?? null) as OverConsumptionTagView | null;
  if (!tag) return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums whitespace-nowrap ${tag.className}`}
      title={tag.title}
      data-severity={tag.severity}
      data-certainty={tag.certainty}
    >
      {tag.text}
    </span>
  );
}

/** A truck's run of trips today, as the run chip shows it. */
export interface RunSummary {
  trip_seq?: number | null;
  trip_count: number;
  loads_completed: number;
  litres_used?: number | null;
  litres_allowance?: number | null;
}

/** "3/4 · 2 loads · 141/150 L" — opens the truck's run. */
export function RunChip({
  run,
  onOpen,
  className = '',
}: {
  run: RunSummary | null | undefined;
  onOpen?: (run: RunSummary) => void;
  /** Extra classes (the phone cards make it a 44 px target). */
  className?: string;
}) {
  if (!run) return <span className="text-xs text-slate-400 italic">—</span>;
  const seqText = run.trip_seq ?? '—';
  const label = `${seqText}/${run.trip_count} · ${run.loads_completed} load${run.loads_completed === 1 ? '' : 's'} · ${Math.round(run.litres_used ?? 0)}/${Math.round(run.litres_allowance ?? 0)} L`;
  const over = Number(run.litres_used) > Number(run.litres_allowance);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(run);
      }}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors hover:bg-slate-100 ${over ? 'border-amber-400 text-amber-800' : 'border-slate-300 text-slate-700'}${className ? ` ${className}` : ''}`}
      title={`Trip ${seqText} of ${run.trip_count} today — open the run`}
      aria-label={`Open run: trip ${seqText} of ${run.trip_count}`}
    >
      {label}
    </button>
  );
}

export function DataAge({ ts, now }: { ts?: string | null; now?: number }) {
  const age = dataAge(ts, now) as { text: string; level: string; className: string; title: string };
  return (
    <span className={`text-xs tabular-nums whitespace-nowrap ${age.className}`} title={age.title}>
      {age.level === 'stale' && <span aria-hidden="true">{OFFLINE_GLYPH} </span>}
      {age.text}
    </span>
  );
}

export function SeverityChip({ severity }: { severity?: string | null }) {
  const meta = severityMeta(severity) as { label: string; className: string };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

// ── Banners, states, layout ─────────────────────────────────────────────────

export function ErrorBanner({ message, onDismiss }: { message?: ReactNode; onDismiss?: () => void }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start"
    >
      <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
      <p className="flex-1 font-medium">{message}</p>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss error"
          className="ml-4 text-red-400 hover:text-red-600"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
}

export function NoticeBanner({ message, onDismiss }: { message?: ReactNode; onDismiss?: () => void }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 flex items-start"
    >
      <FaCheckCircle className="mr-3 mt-0.5 flex-shrink-0 text-emerald-500" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="ml-4 text-emerald-400 hover:text-emerald-600"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
}

export function LoadingState({ label }: { label?: ReactNode }) {
  return (
    <div className="text-center py-12">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
      <p className="mt-4 text-slate-600">{label}</p>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  children,
}: {
  icon?: ComponentType<{ className?: string; size?: number }>;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="text-center py-12">
      {Icon && <Icon className="mx-auto text-slate-300 mb-4" size={32} />}
      <h3 className="text-lg font-medium text-slate-900 mb-2">{title}</h3>
      <div className="text-slate-600 text-sm">{children}</div>
    </div>
  );
}

/** Page shell with the no-permission state (as LexiStarAssets). */
export function NoPermissionPage({ header, message }: { header?: ReactNode; message: ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start">
          <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
          <p className="font-medium">{message}</p>
        </div>
      </div>
    </div>
  );
}

const PageHeader = PageHeaderJs as unknown as ComponentType<PageHeaderProps>;
export { PageHeader };

/**
 * Live / reconnecting / offline indicator for the /tracking socket (LS-09 §3).
 * `compact` (ST-00207, the phone headers): the dot alone while live or connecting, the word
 * only when updates are interrupted — the screen reader hears the state either way.
 */
export function ConnectionIndicator({ status, compact = false }: { status?: string | null; compact?: boolean }) {
  const meta =
    status === 'live'
      ? { dot: 'bg-emerald-500', label: 'Live' }
      : status === 'reconnecting'
        ? { dot: 'bg-amber-500', label: 'Reconnecting' }
        : status === 'offline'
          ? { dot: 'bg-slate-400', label: 'Live updates off' }
          : { dot: 'bg-slate-300', label: 'Connecting' };
  if (compact) {
    const quiet = status !== 'reconnecting' && status !== 'offline';
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600"
        role="status"
        title={meta.label}
      >
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${meta.dot}`} aria-hidden="true" />
        <span className={quiet ? 'sr-only' : undefined}>{meta.label}</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600"
      role="status"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

/** "How was this calculated?" expander (LS-09 §5.5 / §1.1 shown workings). */
export function HowCalculated({
  children,
  label = 'How was this calculated?',
}: {
  children?: ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="touch-target inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
      >
        {open ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
        {label}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 space-y-1 font-mono">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Reason codes (m2-contracts §3.1: kind deviation | delay | override) ────

// LS-03 FR-DEV-004 deviation set — what 023 seeds for every tenant. Used when
// the reason-code API is unavailable so a decision is never blocked on it.
export const FALLBACK_REASON_CODES: Record<string, Array<[string, string]>> = {
  deviation: [
    ['road_closed', 'Road closed'],
    ['accident', 'Accident'],
    ['protest_strike', 'Protest or strike'],
    ['fuel', 'Fuel'],
    ['rest', 'Rest'],
    ['breakdown', 'Breakdown'],
    ['instructed_by_dispatcher', 'Instructed by dispatcher'],
    ['other', 'Other'],
  ],
  override: [
    ['engine_missed_event', 'Engine missed the event'],
    ['device_offline', 'Device offline'],
    ['manual_confirmation', 'Confirmed by phone / on site'],
    ['other', 'Other'],
  ],
};

export interface ReasonCode {
  code: string;
  label: string;
}

interface ReasonCodeRow {
  code: string;
  label: string;
  kind?: string | null;
  active?: boolean;
  sort?: number | null;
}

export function useReasonCodes(kind: string): ReasonCode[] {
  const [codes, setCodes] = useState<ReasonCode[]>(() =>
    (FALLBACK_REASON_CODES[kind] || []).map(([code, label]) => ({ code, label }))
  );
  useEffect(() => {
    let alive = true;
    listReasonCodes(kind)
      .then((res: unknown) => {
        const rows = (pickList(res, 'reason_codes') as ReasonCodeRow[])
          .filter((r) => r.active !== false && (!r.kind || r.kind === kind))
          .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
        if (alive && rows.length) setCodes(rows.map((r) => ({ code: r.code, label: r.label })));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [kind]);
  return codes;
}

// ThemedSelect forwards anything else (autoFocus, isSearchable …) to react-select.
const ThemedSelect = ThemedSelectJs as unknown as ComponentType<
  ThemedSelectProps & { autoFocus?: boolean; isSearchable?: boolean }
>;

export interface ReasonCodeSelectProps {
  codes: ReasonCode[];
  value: string;
  onChange: (code: string) => void;
  label?: string;
  id?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** ST-00207: the phone layouts pass `h-11` (a 44 px control) … */
  className?: string;
  /** … and `false`, so choosing a reason does not open the phone's keyboard. */
  isSearchable?: boolean;
}

export function ReasonCodeSelect({
  codes,
  value,
  onChange,
  label = 'Reason',
  id,
  disabled,
  className = inputClasses,
  ...rest
}: ReasonCodeSelectProps) {
  return (
    <ThemedSelect
      id={id}
      value={value}
      onChange={(e) => onChange(String(e.target.value))}
      className={className}
      aria-label={label}
      disabled={disabled}
      {...rest}
    >
      <option value="">Choose a reason...</option>
      {codes.map((c) => (
        <option key={c.code} value={c.code}>
          {c.label}
        </option>
      ))}
    </ThemedSelect>
  );
}

export interface DateTimeFieldProps {
  id: string;
  value: Date | null;
  onChange: (d: Date | null) => void;
  placeholder?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  /** Phone sheet: quick-time chips (ST-00264), e.g. PLANNING_TIME_PRESETS for a planned start. */
  presets?: readonly TimePreset[];
}

/**
 * A single date + time (planned start, override "until", corridor expiry, split point, "who was
 * driving at"). The shared UnifiedDateTimeRangePicker picks a range; for one instant this is the
 * shared UnifiedDatePicker — on a desktop a field (clearable) opening the date + time popup under
 * it (ST-00294), on a phone a 44 px button opening the bottom sheet; both with the hour and minute
 * grids, "Type a time" and the quick times given. Emits a Date (or null). Label it with
 * <label htmlFor={id}>.
 */
export function DateTimeField({ id, value, onChange, placeholder, minDate, maxDate, presets }: DateTimeFieldProps) {
  return (
    <UnifiedDatePicker
      id={id}
      value={value}
      onChange={(d) => onChange(d)}
      presets={presets}
      withTime
      timeIntervals={15}
      timeFormat="HH:mm"
      dateFormat="yyyy/MM/dd HH:mm"
      placeholder={placeholder}
      minDate={minDate}
      maxDate={maxDate}
      clearable
      className={inputClasses}
      wrapperClassName="w-full"
      autoComplete="off"
    />
  );
}
